import { useState, type FormEvent } from 'react';

import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card } from '../components/ui';

export default function ChangePassword() {
  const { refresh } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (next !== confirm) {
      setMsg({ kind: 'err', text: 'The two new passwords do not match.' });
      return;
    }
    setBusy(true);
    try {
      const r = await api.post<{ otherSessionsRevoked: number }>('/api/v1/auth/change-password', {
        currentPassword: current,
        newPassword: next,
      });
      setMsg({
        kind: 'ok',
        text:
          `Password changed.` +
          (r.otherSessionsRevoked > 0
            ? ` ${r.otherSessionsRevoked} other session${r.otherSessionsRevoked === 1 ? '' : 's'} signed out.`
            : ''),
      });
      setCurrent('');
      setNext('');
      setConfirm('');
      await refresh();
    } catch (err) {
      setMsg({ kind: 'err', text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-lg">
      <Card
        title="Change password"
        subtitle="Any password is accepted. Changing it signs out your other sessions."
      >
        <form onSubmit={submit} className="space-y-4">
          {(
            [
              ['Current password', current, setCurrent, 'current-password'],
              ['New password', next, setNext, 'new-password'],
              ['Confirm new password', confirm, setConfirm, 'new-password'],
            ] as const
          ).map(([label, value, set, ac]) => (
            <label key={label} className="block text-sm text-slate-700">
              {label}
              <input
                type={show ? 'text' : 'password'}
                autoComplete={ac}
                required
                value={value}
                onChange={(e) => set(e.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
          ))}

          {/* One toggle for all three fields — checking a new password against its confirmation
              is the whole reason to reveal it. */}
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={show}
              onChange={(e) => setShow(e.target.checked)}
              className="rounded border-slate-300"
            />
            Show passwords
          </label>

          {msg && (
            <p
              role="alert"
              className={
                msg.kind === 'ok'
                  ? 'rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-700'
                  : 'rounded bg-red-50 px-3 py-2 text-sm text-red-700'
              }
            >
              {msg.text}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Change password'}
          </button>
        </form>
      </Card>
    </div>
  );
}
